import { twMerge } from "tailwind-merge";
import { truncateEnd, truncateMiddle, truncateStart } from "../../../utils/formatting/text";
import { CopyToClipboard } from "../../CopyToClipboard";
import { Skeleton } from "../../Skeleton";
import { Tooltip } from "../../Tooltip";
import type { CellComponentProps, CellEmphasis, LinkBehavior } from "../DataTable.types";
import {
  formatEmptyValue,
  getCellContainerClasses,
  getCellTextClasses,
  getSkeletonSize,
  resolveValue,
  wrapWithLink,
} from "./cellUtils";

export type TextTruncatePosition = "start" | "middle" | "end";

export interface TextCellProps<T = any> extends CellComponentProps<T> {
  prefix?: string;
  suffix?: string;
  emptyText?: string;
  href?: string | ((row: T) => string);
  linkBehavior?: LinkBehavior;
  /** Color variant for links (matches TextLink variants) */
  linkVariant?: "default" | "brand" | "muted" | "unstyled";
  /** Text emphasis (use "strong" for bold) */
  emphasis?: CellEmphasis;
  /**
   * Enable truncation. Can be:
   * - boolean: true for CSS truncation at end (default behavior)
   * - object: { position, length, ellipsis, tooltip } for programmatic truncation
   */
  truncate?:
    | boolean
    | {
        /** Where to truncate the text */
        position?: TextTruncatePosition;
        /** Maximum length including ellipsis */
        length?: number;
        /** Custom ellipsis string */
        ellipsis?: string;
        /** Show tooltip with full text on hover */
        tooltip?: boolean;
      };
  /**
   * Enable copy to clipboard functionality
   * - boolean: true to enable with default settings
   * - function: custom function to extract the value to copy from the row
   */
  copyable?: boolean | ((row: T) => string);
  align?: "left" | "center" | "right";
  className?: string;

  /**
   * PII type for masking (e.g., "email", "phone", "full-name")
   * When provided along with piiEntity, adds data-pii-* attributes for masking
   */
  piiType?: string;

  /**
   * Entity identifier for PII masking (can be a function to extract from row)
   * Example: (row) => `contact:${row.id}`
   */
  piiEntity?: string | ((row: T) => string | undefined);
}

/**
 * TextCell
 *
 * DataTable cell component for displaying text content.
 * Supports prefixes, suffixes, truncation (with position control), copy to clipboard, and optional linking.
 *
 * @example
 * ```tsx
 * // Simple CSS truncation at end (default)
 * { cell: TextCell, cellProps: { truncate: true } }
 *
 * // Truncate in middle for serial numbers with copy
 * {
 *   cell: TextCell,
 *   cellProps: {
 *     truncate: {
 *       position: "middle",
 *       length: 14,
 *       tooltip: true
 *     },
 *     copyable: true
 *   }
 * }
 * ```
 */
export function TextCell<T = any>({
  value,
  row,
  context,
  prefix,
  suffix,
  emptyText = "—",
  href,
  linkBehavior = "hover",
  linkVariant = "default",
  emphasis = "normal",
  truncate = true,
  copyable = false,
  align = "left",
  className,
  piiType,
  piiEntity,
}: TextCellProps<T>) {
  const { isLoading } = context;

  if (isLoading) {
    return <Skeleton className={getSkeletonSize(emphasis)} />;
  }

  const displayValue = formatEmptyValue(value, emptyText);
  const resolvedHref = resolveValue(href, row);
  const piiEntityValue = resolveValue(piiEntity, row);

  // Determine truncation behavior
  let truncatedValue = displayValue;
  let shouldShowTooltip = false;
  let useCssTruncate = false;

  if (truncate && displayValue && typeof displayValue === "string") {
    if (typeof truncate === "boolean") {
      // Simple boolean: use CSS truncation
      useCssTruncate = true;
    } else {
      // Object with options: use programmatic truncation
      const { position = "end", length = 16, ellipsis = "...", tooltip = true } = truncate;

      // Only truncate if the text is longer than the specified length
      if (displayValue.length > length) {
        switch (position) {
          case "start":
            truncatedValue = truncateStart(displayValue, length);
            break;
          case "middle":
            truncatedValue = truncateMiddle(displayValue, length);
            break;
          case "end":
            truncatedValue = truncateEnd(displayValue, length, ellipsis);
            break;
        }
        shouldShowTooltip = tooltip;
      }
    }
  }

  // Build PII attributes object if both piiType and piiEntity are provided
  const piiAttrs =
    piiType && piiEntityValue
      ? {
          "data-pii-type": piiType,
          "data-pii-entity": piiEntityValue,
        }
      : {};
  const hasPiiAttrs = Object.keys(piiAttrs).length > 0;

  // Build text span with optional PII attributes
  const textSpan = (
    <span className={useCssTruncate ? "truncate" : ""} {...piiAttrs}>
      {truncatedValue}
    </span>
  );

  // Wrap with Tooltip if truncation tooltip is enabled
  // Uses Tooltip component instead of native title attribute to allow PII masking by Curtain
  const textWithTooltip = shouldShowTooltip ? (
    <Tooltip content={hasPiiAttrs ? <span {...piiAttrs}>{String(displayValue)}</span> : String(displayValue)}>
      {textSpan}
    </Tooltip>
  ) : (
    textSpan
  );

  const textContent = (
    <>
      {prefix && <span className="text-text-muted">{prefix}</span>}
      {textWithTooltip}
      {suffix && <span className="text-text-muted">{suffix}</span>}
    </>
  );

  // Determine if we should wrap with copy functionality
  let content = textContent;
  if (copyable && displayValue && displayValue !== emptyText) {
    const copyValue = typeof copyable === "function" ? copyable(row) : String(displayValue);

    content = (
      <CopyToClipboard value={copyValue} size="sm">
        {textContent}
      </CopyToClipboard>
    );
  }

  // If href is provided, wrap with link behavior
  if (resolvedHref) {
    // For links, don't apply text styling to the container, only alignment
    const containerClasses = getCellContainerClasses(align, className);
    return (
      <div className={containerClasses}>
        {wrapWithLink(content, resolvedHref, row, linkBehavior, linkVariant, emphasis)}
      </div>
    );
  }

  // For non-links, apply text styling to container
  const baseClasses = getCellContainerClasses(align, twMerge(getCellTextClasses(emphasis), className));

  return <div className={baseClasses}>{content}</div>;
}
